/**
 * Class (board) search typeahead — backed by:
 *   GET /midl/api/v3/board/typeahead?code={query}
 *
 * Backend response is a `BaseResponseDto` envelope wrapping
 * `BoardResponseDto[]` — we tolerate both `{ data: [...] }` and a bare array.
 */
import api from '@/api/client';
import { servicePath } from '@/config/env';
import { toBoardMembershipStatus } from '@/features/class/boardsApi';
import type { SearchClassResult } from '@/features/search/types';

interface ApiBoardUser {
  readonly id?: number;
  readonly userId?: number;
  readonly username?: string;
  readonly name?: string;
  readonly surName?: string;
  readonly fullName?: string;
}

interface ApiBoard {
  readonly id?: number;
  readonly userId?: number;
  readonly name?: string;
  readonly code?: string;
  readonly isPrivate?: boolean;
  readonly isJoined?: number | string;
  readonly boardOwnerInfo?: ApiBoardUser;
}

interface ApiBoardTypeaheadResponse {
  readonly statusCode?: string;
  /** Backend returns a single object for exact-code matches and an array otherwise. */
  readonly data?: ApiBoard | readonly ApiBoard[];
}

export interface ClassTypeaheadParams {
  readonly query: string;
  readonly signal?: AbortSignal;
}

/** Deterministic accent color from the class code so rows are visually stable. */
const DOT_PALETTE = [
  'bg-[#60a5fa]',
  'bg-[#c084fc]',
  'bg-[#f472b6]',
  'bg-[#f87171]',
  'bg-[#fb923c]',
  'bg-[#fbbf24]',
  'bg-[#34d399]',
  'bg-[#22d3ee]',
];

function dotClassNameFor(code: string): string {
  let hash = 0;
  for (let i = 0; i < code.length; i += 1) {
    hash = (hash * 31 + code.charCodeAt(i)) | 0;
  }
  const index = Math.abs(hash) % DOT_PALETTE.length;
  return DOT_PALETTE[index];
}

function ownerNameOf(owner: ApiBoardUser | undefined): string {
  if (!owner) return '';
  const first = (owner.name ?? '').trim();
  const rawLast = (owner.surName ?? '').trim();
  // Some payloads return the numeric user id in `surName` — ignore that case.
  const last = rawLast && !/^\d+$/.test(rawLast) ? rawLast : '';
  const combined = `${first} ${last}`.trim();
  return combined || (owner.fullName ?? '').trim() || owner.username || '';
}

function toSearchClassResult(item: ApiBoard): SearchClassResult | null {
  const code = (item.code ?? '').trim();
  if (!code) return null;
  const owner = item.boardOwnerInfo;
  return {
    classId: String(item.id ?? code),
    code,
    name: (item.name ?? code).trim(),
    dotClassName: dotClassNameFor(code),
    ownerUsername: owner?.username ?? '',
    ownerName: ownerNameOf(owner),
    ownerUserId:
      item.userId != null
        ? String(item.userId)
        : owner?.userId != null
          ? String(owner.userId)
          : owner?.id != null
            ? String(owner.id)
            : undefined,
    isPrivate: Boolean(item.isPrivate),
    isJoined: toBoardMembershipStatus(item.isJoined) ?? undefined,
  };
}

/** Fetch class typeahead results. Returns `[]` for empty queries. */
export async function searchClassesTypeahead({
  query,
  signal,
}: ClassTypeaheadParams): Promise<SearchClassResult[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];

  const { data } = await api.get<ApiBoardTypeaheadResponse | ApiBoard | readonly ApiBoard[]>(
    servicePath('midl', 'board/typeahead'),
    {
      params: { code: trimmed },
      signal,
    },
  );

  // Backend variations observed:
  //   • { statusCode, data: BoardResponseDto[] }   ← typical list match
  //   • { statusCode, data: BoardResponseDto }     ← single exact-code match
  //   • BoardResponseDto[] / BoardResponseDto      ← bare payload (older builds)
  let rows: readonly ApiBoard[] = [];
  if (Array.isArray(data)) {
    rows = data as readonly ApiBoard[];
  } else if (data && typeof data === 'object') {
    const envelope = data as ApiBoardTypeaheadResponse;
    if (Array.isArray(envelope.data)) {
      rows = envelope.data;
    } else if (envelope.data && typeof envelope.data === 'object') {
      rows = [envelope.data as ApiBoard];
    } else if ('code' in (data as ApiBoard) || 'id' in (data as ApiBoard)) {
      // Bare BoardResponseDto returned without the envelope.
      rows = [data as ApiBoard];
    }
  }

  return rows
    .map(toSearchClassResult)
    .filter((value: SearchClassResult | null): value is SearchClassResult => value !== null);
}
